import React, { useState } from "react";
import { Badge, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import DstConfirm from "../../../../components/DstConfirm/DstConfirm";
import InfoRow from "../../../../components/InfoRow/InfoRow";
import { useSession } from "../../../../contexts/SessionContext";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import EditExerciseModal from "./EditExerciseModal/EditExerciseModal";
import EditExerciseResourcesModal from "./EditExerciseResourcesModal/EditExerciseResourcesModal";
import { DifficultyBadge, navigateToExerciseFilePreSignedUrl, removeExerciseResources } from "./MainInfo.function";

const MainInfo = ({ exercise, onRefresh }: any) => {
  const { language } = useSession();
  const [isRemoving, setIsRemoving] = useState(false);
  const [showEditExerciseModal, setShowEditExerciseModal] = useState(false);
  const [showEditResourcesModal, setShowEditResourcesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <Row>
      <Col lg={12} className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="m-bold">{translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.TITLE")}</div>

          <div className="d-flex">
            <PermissionComponent neededPermission={[{ methods: ["PATCH"], route: "/exercises/*" }]}>
              <DstButton
                btnClass="me-2"
                btnImageBefore={<span className="dst-icon-trash-01 me-2" />}
                value={translate(language, "GLOBAL.DELETE")}
                variant="danger"
                clickFunction={() => alert("WIP")}
              />
              <DstButton
                variant="secondary"
                btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
                value={translate(language, "GLOBAL.EDIT")}
                clickFunction={() => setShowEditExerciseModal(true)}
              />
            </PermissionComponent>
          </div>
        </div>

        <EditExerciseModal
          show={showEditExerciseModal}
          onClose={() => setShowEditExerciseModal(false)}
          exercise={exercise}
          language={language}
          onSuccess={onRefresh}
        />

        <Row>
          <Col md={6}>
            {InfoRow(
              "dst-icon-hash-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.ID"),
              exercise.id
            )}

            {InfoRow("dst-icon-tag-01", translate(language, "GLOBAL.NAME"), exercise.name)}

            {InfoRow("dst-icon-file-02", translate(language, "GLOBAL.DESCRIPTION"), exercise.description)}

            {InfoRow(
              "dst-icon-star-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.DIFFICULTY"),
              <DifficultyBadge difficulty={exercise.difficulty} />
            )}

            {InfoRow("dst-icon-clock", translate(language, "GLOBAL.DURATION"), `${exercise.duration} min`)}

            {InfoRow("dst-icon-file-code-01", translate(language, "GLOBAL.TYPE"), exercise.type)}

            {InfoRow("dst-icon-translate-01", translate(language, "GLOBAL.LANGUAGE"), exercise.language)}

            {InfoRow(
              "dst-icon-paperclip",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.FILENAME"),
              exercise.filename,
              <span
                className="blue-font dst-icon-download-01 clickable"
                onClick={() => navigateToExerciseFilePreSignedUrl(exercise.id, language)}
              />
            )}
          </Col>
          <Col md={6}>
            {InfoRow(
              "dst-icon-info-circle",
              translate(language, "GLOBAL.STATUS"),
              exercise.is_deprecated ? (
                <Badge bg="warning">{translate(language, "GLOBAL.DEPRECATED")}</Badge>
              ) : (
                <Badge bg="success">{translate(language, "GLOBAL.ACTIVE")}</Badge>
              )
            )}

            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.CREATED"),
              formatDateHumanly(language, exercise.dt_created, true)
            )}

            {exercise.dt_updated &&
              InfoRow(
                "dst-icon-calendar-date",
                translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.UPDATED"),
                formatDateHumanly(language, exercise.dt_updated, true)
              )}

            {InfoRow(
              "dst-icon-user-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.OWNER"),
              <Link to={`/user/${exercise.owner.id}`} className="text-decoration-none">
                {exercise.owner.full_name}
              </Link>
            )}

            {InfoRow(
              "dst-icon-box",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.ORIGIN_MODULE"),
              exercise.origin_module ? (
                <Link to={`/module/${exercise.origin_module.id}`} className="text-decoration-none">
                  {exercise.origin_module.name}
                </Link>
              ) : (
                "—"
              )
            )}

            {InfoRow(
              "dst-icon-cpu-chip-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.INSTANCE_TYPE"),
              exercise.instance_type
            )}

            {InfoRow(
              "dst-icon-server-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.INSTANCE_PROVIDER"),
              exercise.instance_provider
            )}
          </Col>
        </Row>
      </Col>

      <Col lg={12}>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <div className="m-bold">{translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.TITLE")}</div>
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="resources-default-values-tooltip">
                  {translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.DEFAULT_VALUES")}
                </Tooltip>
              }
            >
              <span className="dst-icon-info-circle ms-2 text-muted clickable" />
            </OverlayTrigger>
          </div>

          <div className="d-flex">
            <PermissionComponent neededPermission={[{ methods: ["PATCH"], route: "/exercises/*" }]}>
              {(exercise.required_resources?.cpu ||
                exercise.required_resources?.memory ||
                exercise.required_resources?.ephemeral_storage) && (
                <DstButton
                  btnClass="me-2"
                  btnImageBefore={<span className="dst-icon-trash-01 me-2" />}
                  value={translate(language, "GLOBAL.DELETE")}
                  variant="danger"
                  clickFunction={() => setShowDeleteModal(true)}
                  loading={isRemoving}
                  disabled={isRemoving}
                />
              )}
              <DstButton
                variant="secondary"
                btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
                value={translate(language, "GLOBAL.EDIT")}
                clickFunction={() => setShowEditResourcesModal(true)}
              />
            </PermissionComponent>
          </div>
        </div>

        <DstConfirm
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={() => removeExerciseResources(exercise.id, setIsRemoving, onRefresh, language)}
          title={translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.MODAL_DELETION.CONFIRM_DELETE_TITLE")}
          messageConfirm={translate(
            language,
            "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.MODAL_DELETION.CONFIRM_DELETE_TEXT"
          )}
          confirmTextButton={translate(language, "GLOBAL.DELETE")}
        />

        <EditExerciseResourcesModal
          show={showEditResourcesModal}
          onClose={() => setShowEditResourcesModal(false)}
          exercise={exercise}
          language={language}
          onSuccess={onRefresh}
        />

        <Row>
          <Col md={6}>
            {InfoRow(
              "dst-icon-settings-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.FIELDS.CPU"),
              exercise.required_resources?.cpu ||
                translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.NO_VALUE")
            )}
            {InfoRow(
              "dst-icon-settings-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.FIELDS.MEMORY"),
              exercise.required_resources?.memory ||
                translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.NO_VALUE")
            )}
            {InfoRow(
              "dst-icon-database-01",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.FIELDS.STORAGE"),
              exercise.required_resources?.ephemeral_storage ||
                translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.NO_VALUE"),
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="time-slots-tooltip">
                    {translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.FIELDS.STORAGE_NOTE")}
                  </Tooltip>
                }
              >
                <span className="dst-icon-info-circle ms-1 text-muted clickable" />
              </OverlayTrigger>
            )}
          </Col>
          <Col md={6}>
            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.FIELDS.CREATED"),
              exercise.required_resources?.dt_created
                ? formatDateHumanly(language, exercise.required_resources.dt_created, true)
                : null
            )}
            {exercise.required_resources?.dt_updated &&
              InfoRow(
                "dst-icon-calendar-date",
                translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.RESOURCES.FIELDS.UPDATED"),
                formatDateHumanly(language, exercise.required_resources.dt_updated, true)
              )}
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default MainInfo;
